import { getDateFormatter } from '@messageformat/date-skeleton';
import { format } from 'date-fns';
import getFormattingLocale from './getFormattingLocale';
import getDateFnsLocale from './getDateFnsLocale';

/**
 * Formats a date value according to locale and format options.
 *
 * Supports three formatting approaches:
 * 1. Intl.DateTimeFormatOptions - Standard web API for locale-aware formatting
 * 2. ICU skeleton patterns - Simple patterns without separators (e.g., 'yMd', 'yMMMd')
 * 3. Skeleton patterns with separators - Format strings with exact order (e.g., 'yyyy-MM-dd', 'MMM yyyy')
 *
 * @param value - Date value to format (Date, number timestamp, or ISO string)
 * @param options - Format options (object) or skeleton pattern (string)
 * @param defaultValue - Value to return if date is invalid or not provided
 * @returns Formatted date string or defaultValue
 *
 * @example
 * // ============================================================================
 * // DEFAULT FORMAT (no options provided)
 * // ============================================================================
 * formatDate(new Date())
 * // en: "02/26/24", fr-CA: "24/02/26"
 *
 * @example
 * // ============================================================================
 * // INTL.DATETIMEFORMATOPTIONS (locale-aware, order varies by locale)
 * // ============================================================================
 *
 * // Short month name with numeric day and year
 * formatDate('2024-02-26', { day: 'numeric', month: 'short', year: 'numeric' })
 * // en: "Feb 26, 2024", fr-CA: "26 févr. 2024"
 *
 * // Long month name
 * formatDate('2024-02-26', { month: 'long', day: 'numeric', year: 'numeric' })
 * // en: "February 26, 2024", fr-CA: "26 février 2024"
 *
 * // Short date style
 * formatDate('2024-02-26', { dateStyle: 'short' })
 * // en: "2/26/24", fr-CA: "24-02-26"
 *
 * // Date with 2-digit year, month, and day
 * formatDate('2024-02-26', { year: '2-digit', month: '2-digit', day: '2-digit' })
 * // en: "02/26/24", fr-CA: "26/02/24"
 *
 * // Time formatting (12h vs 24h based on locale)
 * formatDate('2024-02-26T14:30:00', { hour: '2-digit', minute: '2-digit' })
 * // en: "02:30 PM", fr-CA: "14:30"
 *
 * // Weekday name
 * formatDate('2024-02-26', { weekday: 'long' })
 * // en: "Monday", fr-CA: "lundi"
 *
 * @example
 * // ============================================================================
 * // ICU SKELETON PATTERNS (locale-aware, order varies by locale)
 * // ============================================================================
 * formatDate('2024-02-26', 'yMd')
 * // en: "2/26/2024", fr-CA: "26/2/2024"
 *
 * @example
 * // ============================================================================
 * // SKELETON PATTERNS WITH SEPARATORS (exact order preserved)
 * // ============================================================================
 *
 * // ISO date format
 * formatDate('2024-02-26', 'yyyy-MM-dd')
 * // en: "2024-02-26", fr-CA: "2024-02-26"
 *
 * // Month name with year (month name is locale-aware)
 * formatDate('2024-02-26', 'MMM yyyy')
 * // en: "Feb 2024", fr-CA: "févr. 2024"
 */
function formatDate(
  value?: Date | number | string | null,
  options?: Intl.DateTimeFormatOptions | string,
  defaultValue: string = ''
): string {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }

  const date = parseDate(value);
  if (!date) {
    return defaultValue;
  }

  const locale = getFormattingLocale();

  // String = skeleton pattern, Object = Intl options
  if (typeof options === 'string') {
    return formatWithSkeleton(date, options, locale);
  }

  const formatOptions: Intl.DateTimeFormatOptions = options ?? {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  };

  return new Intl.DateTimeFormat(locale, formatOptions).format(date);
}

function parseDate(value: Date | number | string): Date | null {
  let date: Date;

  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'number') {
    date = new Date(value);
  } else {
    // Handle ISO dates with space separator and dates without time part
    const normalized = value.replace(' ', 'T');
    const withTime = normalized.includes('T')
      ? normalized
      : `${normalized}T00:00:00`;
    date = new Date(withTime);
  }

  return isNaN(date.getTime()) ? null : date;
}

function formatWithSkeleton(
  date: Date,
  skeleton: string,
  locale: string
): string {
  // If skeleton is a simple ICU pattern (no separators), use ICU formatter
  // Examples: 'yMd', 'yMMMd', 'Hms'
  if (!/[/:\-,\s]/.test(skeleton)) {
    return getDateFormatter(locale, skeleton)(date);
  }

  // Otherwise, use date-fns format which handles patterns with separators natively
  // Examples: 'yyyy-MM-dd', 'MM/dd/yy', 'MMM yyyy', 'HH:mm:ss'
  return format(date, skeleton, { locale: getDateFnsLocale() });
}

export default formatDate;
